/**
 * Modal "Esqueci minha senha" (E35.F2). // ux-neutrality:allow doc-comment
 *
 * Coleta o e-mail e chama POST /api/auth/forgot-password. A confirmação é // ux-neutrality:allow doc-comment
 * SEMPRE neutra ("se houver conta, enviamos um link") — não revela se a conta // ux-neutrality:allow doc-comment
 * existe (postura LGPD do reset). Só erro de rede mostra mensagem de falha. // ux-neutrality:allow doc-comment
 */
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { forgotPassword, ResetPasswordError } from '@/lib/auth';

export interface ForgotPasswordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** E-mail já digitado no login, pré-preenchido por conveniência. */
  initialEmail?: string;
}

export function ForgotPasswordModal({
  open,
  onOpenChange,
  initialEmail = '',
}: ForgotPasswordModalProps) {
  const apiClient = useApiClient();
  const t = ptBR.login.forgot;

  const [email, setEmail] = useState(initialEmail);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(evt: FormEvent<HTMLFormElement>) {
    evt.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await forgotPassword(apiClient, email);
      setSent(true);
    } catch (err) {
      // Falha real (rede/inesperada) — não vaza existência da conta.
      const reason = err instanceof ResetPasswordError ? err.reason : 'unknown';
      setError(reason === 'network' ? t.errorNetwork : ptBR.errors.generic);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="forgot-modal">
        {sent ? (
          <>
            <DialogHeader>
              <DialogTitle>{t.sentTitle}</DialogTitle>
              <DialogDescription>{t.sentBody}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                data-testid="forgot-sent-confirm"
              >
                {t.sentConfirm}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle>{t.title}</DialogTitle>
              <DialogDescription>{t.body}</DialogDescription>
            </DialogHeader>
            <Field label={t.emailLabel} htmlFor="forgot-email-input">
              <Input
                id="forgot-email-input"
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.emailPlaceholder}
                disabled={submitting}
                data-testid="forgot-email"
              />
            </Field>
            {error ? (
              <p
                role="alert"
                data-testid="forgot-error"
                className="border border-danger-500 bg-danger-50 px-3 py-2 text-body-sm text-danger-700"
              >
                {error}
              </p>
            ) : null}
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
                data-testid="forgot-cancel"
              >
                {t.cancel}
              </Button>
              <Button type="submit" disabled={submitting} data-testid="forgot-submit">
                {submitting ? t.submitting : t.submit}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
